import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Search, Image, Upload, X, Check, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProducts() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const fileInputRef = useRef(null);

  const searchProducts = async () => {
    if (!search || search.length < 2) {
      toast.error("En az 2 karakter girin");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/seller/catalog/search?q=${encodeURIComponent(search)}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) {
      toast.error("Arama başarısız");
    }
    setLoading(false);
  };

  const openImageEditor = (product) => {
    setSelectedProduct(product);
    setNewImageUrl(product.image_url?.split("|")[0] || "");
    setShowImageModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Sadece resim dosyası yükleyebilirsiniz");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya boyutu 5MB'dan küçük olmalı");
      return;
    }
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        const res = await fetch(`${API_URL}/api/admin/products/${selectedProduct.id}/upload-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ image: base64 })
        });
        
        if (res.ok) {
          const data = await res.json();
          toast.success("Resim yüklendi!");
          setProducts(products.map(p => 
            p.id === selectedProduct.id ? {...p, image_url: data.image_url} : p
          ));
          setShowImageModal(false);
        } else {
          toast.error("Yükleme başarısız");
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Bir hata oluştu");
      setUploading(false);
    }
  };

  const updateImageUrl = async () => {
    if (!newImageUrl) {
      toast.error("Resim URL'si girin");
      return;
    }
    
    setUploading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/products/${selectedProduct.id}/image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ image_url: newImageUrl })
      });
      
      if (res.ok) {
        toast.success("Resim güncellendi!");
        setProducts(products.map(p => 
          p.id === selectedProduct.id ? {...p, image_url: newImageUrl} : p
        ));
        setShowImageModal(false);
      } else {
        toast.error("Güncelleme başarısız");
      }
    } catch (err) {
      toast.error("Bir hata oluştu");
    }
    setUploading(false);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ürün Yönetimi</h1>
        <p className="text-gray-500">Katalog ürünlerinin resimlerini düzenleyin</p>
      </div>

      {/* Bilgi */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Hatalı Ürün Resimleri</p>
          <p className="text-sm text-amber-700">SKU veya OEM kodu ile arayın, resmi hatalı olan ürünü bulun ve yeni resim yükleyin veya URL girin.</p>
        </div>
      </div>

      {/* Arama */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="SKU, OEM kodu veya ürün adı ile ara (örn: ALTAY-MHR-04140)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchProducts()}
              className="w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            onClick={searchProducts}
            disabled={loading}
            className="px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:opacity-50"
          >
            {loading ? "..." : "Ara"}
          </button>
        </div>
      </div>

      {/* Ürün Listesi */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resim</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün Bilgisi</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">OEM Kodu</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marka</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {products.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-12 text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>Ürün aramak için yukarıdaki arama kutusunu kullanın</p>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {product.image_url ? (
                      <img 
                        src={product.image_url.split("|")[0]} 
                        alt="" 
                        className="w-16 h-16 object-cover rounded-lg border"
                        onError={(e) => e.target.src = "/placeholder.png"}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-sm line-clamp-2">{product.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{product.sku}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-orange-600">{product.oem_code || "-"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{product.brand || "-"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openImageEditor(product)}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                    >
                      <Image className="w-4 h-4" />
                      Resim Düzenle
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Resim Düzenleme Modal */}
      {showImageModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Ürün Resmini Düzenle</h2>
                <button onClick={() => setShowImageModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1 truncate">{selectedProduct.name}</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Mevcut Resim */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Mevcut Resim</p>
                <div className="flex justify-center">
                  {selectedProduct.image_url ? (
                    <img 
                      src={selectedProduct.image_url.split("|")[0]} 
                      alt="" 
                      className="w-40 h-40 object-cover rounded-lg border"
                    />
                  ) : (
                    <div className="w-40 h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Yeni Resim Yükle */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Yeni Resim Yükle</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Upload className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-600">{uploading ? "Yükleniyor..." : "Bilgisayardan Resim Seç"}</span>
                </button>
              </div>

              {/* URL ile güncelle */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">veya URL Girin</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-4 py-2 border-2 rounded-lg"
                  />
                  <button
                    onClick={updateImageUrl}
                    disabled={uploading}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowImageModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
